/**
 * Win Log: tracks citation flips, score improvements, and hallucination fixes.
 * Persisted to localStorage and synced to Supabase.
 *
 * "Radar users have logged X citation wins across ChatGPT, Perplexity, Claude, and Gemini."
 * This stat becomes marketing.
 */

import type { WinLogEntry, SavedRun } from '@/types/radar'

const STORAGE_KEY = 'pm_win_log'

// ── Storage ────────────────────────────────────────────────────────────────────

export function loadWinLog(): WinLogEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as WinLogEntry[]) : []
  } catch {
    return []
  }
}

export function saveWinLog(entries: WinLogEntry[]): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    /* localStorage full or unavailable */
  }
}

export function addWin(entry: Omit<WinLogEntry, 'id'>): WinLogEntry[] {
  const win: WinLogEntry = {
    ...entry,
    id: `win-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  }
  const existing = loadWinLog()
  const updated = [win, ...existing].slice(0, 100)
  saveWinLog(updated)
  return updated
}

export function removeWin(id: string): WinLogEntry[] {
  const existing = loadWinLog()
  const updated = existing.filter(w => w.id !== id)
  saveWinLog(updated)
  return updated
}

// ── Detection ──────────────────────────────────────────────────────────────────

/**
 * Detect citation flips between two runs.
 * Returns providers that went from "not cited" to "cited".
 */
export function detectCitationFlips(
  previousRun: SavedRun,
  currentToolData: Record<
    string,
    { status: string; data: Record<string, unknown> | null }
  >
): { provider: string; description: string }[] {
  const flips: { provider: string; description: string }[] = []

  const prevCitation = previousRun.toolData?.citation as {
    providers?: { provider: string; overallMentionRate: number }[]
  } | null
  const currCitationState = currentToolData?.citation
  const currCitation =
    currCitationState?.status === 'done'
      ? (currCitationState.data as {
          providers?: { provider: string; overallMentionRate: number }[]
        } | null)
      : null

  if (!prevCitation?.providers || !currCitation?.providers) return flips

  for (const curr of currCitation.providers) {
    const prev = prevCitation.providers.find(
      p => p.provider.toLowerCase() === curr.provider.toLowerCase()
    )
    if (prev && prev.overallMentionRate === 0 && curr.overallMentionRate > 0) {
      flips.push({
        provider: curr.provider,
        description: `${curr.provider}: Not cited -> Cited`,
      })
    }
  }

  return flips
}

/**
 * Detect "fix resolved" states: checks that were failing in previous run
 * but are now passing in the current run.
 */
export function detectResolvedFixes(
  previousRun: SavedRun,
  currentToolScores: Record<string, { score: number; grade: string }>
): {
  tool: string
  previousScore: number
  currentScore: number
  previousDate: number
}[] {
  const resolved: {
    tool: string
    previousScore: number
    currentScore: number
    previousDate: number
  }[] = []

  for (const [key, prev] of Object.entries(previousRun.toolScores)) {
    const curr = currentToolScores[key]
    if (!curr) continue
    // "Failing" = score < 50, "Passing" = score >= 50
    if (prev.score < 50 && curr.score >= 50) {
      resolved.push({
        tool: key,
        previousScore: prev.score,
        currentScore: curr.score,
        previousDate: previousRun.timestamp,
      })
    }
  }

  return resolved
}
